import {KpiScore} from '@hconnect/uikit/src/lib2'
import {InfoOutlined} from '@mui/icons-material'
import {Box, Stack, Tooltip, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {DOWNTIME_ANALYTICS_WIDGET_NAMESPACE} from '../consts'

type Props = {
  longKilnStoppageCount: number
  longKilnStoppageRcfaCount: number
  lateLongKilnStoppageRcfaCount: number
  overdueKilnStoppageRcfaCount: number
  totalOverdueKilnStoppageRcfaCount: number
  timeRangeLabel: string
}

export const RcfaSummary = ({
  longKilnStoppageCount,
  longKilnStoppageRcfaCount,
  overdueKilnStoppageRcfaCount,
  totalOverdueKilnStoppageRcfaCount,
  lateLongKilnStoppageRcfaCount,
  timeRangeLabel
}: Props) => {
  const {t} = useTranslation(DOWNTIME_ANALYTICS_WIDGET_NAMESPACE)
  const isOverdueWarningShown =
    totalOverdueKilnStoppageRcfaCount > 0 &&
    overdueKilnStoppageRcfaCount !== totalOverdueKilnStoppageRcfaCount
  const overdueVariant = totalOverdueKilnStoppageRcfaCount > 0 ? 'negative' : 'neutral'

  return (
    <Stack direction="row" spacing={3} useFlexGap>
      <StackItem>
        <KpiScore
          label={t('rcfaSummary.label.mandatory')}
          value={`${longKilnStoppageRcfaCount} / ${longKilnStoppageCount}`}
          data-test-id="mandatory-rcfa"
        />
      </StackItem>
      <StackItem>
        <Tooltip
          title={
            isOverdueWarningShown ? (
              <OverdueLabelTooltip
                overdue={overdueKilnStoppageRcfaCount}
                totalOverdue={totalOverdueKilnStoppageRcfaCount}
                timeRangeLabel={timeRangeLabel}
              />
            ) : null
          }
          arrow
          placement="right"
        >
          <span>
            <KpiScore
              label={t('rcfaSummary.label.overdue')}
              labelIcon={isOverdueWarningShown ? <InfoOutlined /> : undefined}
              value={totalOverdueKilnStoppageRcfaCount}
              variant={overdueVariant}
              data-test-id="overdue-rcfa"
            />
          </span>
        </Tooltip>
      </StackItem>
      <StackItem>
        <Stack spacing={0.5}>
          <Tooltip
            arrow
            placement="right"
            title={
              <Typography fontWeight="medium">{t('rcfaSummary.label.lateTooltipTitle')}</Typography>
            }
          >
            <span>
              <KpiScore
                labelIcon={<InfoOutlined />}
                label={t('rcfaSummary.label.late')}
                value={lateLongKilnStoppageRcfaCount}
                data-test-id="late-rcfa"
              />
            </span>
          </Tooltip>
        </Stack>
      </StackItem>
    </Stack>
  )
}

const StackItem: React.FC<{children: React.ReactNode}> = ({children}) => (
  <Box
    sx={{
      display: 'flex',
      flex: '1 0 0'
    }}
  >
    {children}
  </Box>
)

const OverdueLabelTooltip = ({
  overdue,
  totalOverdue,
  timeRangeLabel
}: {
  overdue: number
  totalOverdue: number
  timeRangeLabel: string
}) => {
  const {t} = useTranslation(DOWNTIME_ANALYTICS_WIDGET_NAMESPACE)

  return (
    <Stack spacing={0.5}>
      <Typography fontWeight="medium">
        {`${totalOverdue} ${t('rcfaSummary.label.overdue')} (${t('rcfaSummary.label.allTime')})`}
      </Typography>
      <Typography>{`${overdue} ${t('rcfaSummary.label.overdue')} (${timeRangeLabel})`}</Typography>
    </Stack>
  )
}
